import { DateOnly } from '@if/core/date'
import { EOriginSystemId, EPaymentFrequency } from '@if/core/types'
import { formatDate } from '@if/core/utils'
import {
  ControlledCheckbox,
  ControlledDateOnlyPicker,
  ControlledNumberField,
  ControlledSelect,
  createLoadingStore,
  FieldPrerequisiteSpec,
  FieldReadyGate,
  FieldReadyGateProvider,
  renderSelectMenuItems,
  useFormContainerContext,
  useReconcileFieldToOptions,
  useReportFieldLoading,
  useTranslatedOptionEnum,
} from '@if/mui'
import { Alert, Grid } from '@mui/material'
import {
  FirstPaymentDateOptionsDto,
  useGetFirstPaymentDateOptions,
} from '@src/data/api/credit-application/credit-application-api'
import { useFeatureFlags } from '@src/data/selectors/app-config-selectors'
import { memo, ReactNode, useEffect, useMemo, useRef } from 'react'
import { Control, FieldPath, FieldPathByValue, FieldValues, useController, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import {
  BaseWorksheetFormConfig,
  BaseWorksheetFormConfigProvider,
  findMissingFinancingConfigKeys,
  useBaseWorksheetFormConfig,
  WorksheetSimulationPricing,
  WorksheetSimulationPricingProvider,
} from './BaseWorksheetForm.config'
import { isPromoPlan } from './BaseWorksheetForm.helpers'
import TermPlanSelect, { StoredTermAndPlan } from './TermPlanSelect'
const cellSize = { xs: 12, md: 4 } as const

const FIELD_READY_GATE_CONFIG = {
  fillPrerequisitesFirstKey: 'worksheetCommon.fillPrerequisitesFirst',
  waitingForPrerequisitesKey: 'worksheetCommon.waitingForPrerequisites',
} as const

const FIRST_PAYMENT_DATE_PICKER_THRESHOLD = 12

interface BaseWorksheetFormProps {
  config: BaseWorksheetFormConfig
  simulationPricing?: WorksheetSimulationPricing
  children: ReactNode
}

const BaseWorksheetForm = ({ config, simulationPricing, children }: BaseWorksheetFormProps) => {
  const { t } = useTranslation()
  const { setBusy } = useFormContainerContext()
  const loadingStore = useMemo(() => createLoadingStore(), [])

  const setBusyRef = useRef(setBusy)
  setBusyRef.current = setBusy
  useEffect(() => {
    let last = loadingStore.isAnyLoading()
    setBusyRef.current('worksheetFields', last)
    const unsubscribe = loadingStore.subscribe(() => {
      const next = loadingStore.isAnyLoading()
      if (next === last) return
      last = next
      setBusyRef.current('worksheetFields', next)
    })
    return () => {
      unsubscribe()
      setBusyRef.current('worksheetFields', false)
    }
  }, [loadingStore])

  const missing = useMemo(() => findMissingFinancingConfigKeys(config), [config])
  const isConfigValid = missing.length === 0
  useEffect(() => {
    setBusy('configInvalid', !isConfigValid)
    return () => setBusy('configInvalid', false)
  }, [setBusy, isConfigValid])

  return (
    <BaseWorksheetFormConfigProvider value={config}>
      <WorksheetSimulationPricingProvider value={simulationPricing ?? null}>
        <FieldReadyGateProvider store={loadingStore} config={FIELD_READY_GATE_CONFIG}>
          {!isConfigValid && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {t('worksheetCommon.errors.configInvalid', { missing: missing.join(', ') })}
            </Alert>
          )}
          <Grid container spacing={2.5}>
            {children}
          </Grid>
        </FieldReadyGateProvider>
      </WorksheetSimulationPricingProvider>
    </BaseWorksheetFormConfigProvider>
  )
}

interface AmountRequestedProps<T extends FieldValues & { amountRequested: number | null }> {
  control: Control<T>
  label?: string
  disabled?: boolean
}

const AmountRequested = <T extends FieldValues & { amountRequested: number | null }>({
  control,
  label,
  disabled,
}: AmountRequestedProps<T>) => {
  const { t } = useTranslation()
  return (
    <Grid size={cellSize}>
      <ControlledNumberField
        name={'amountRequested' as FieldPathByValue<T, number>}
        control={control}
        label={label ?? t('worksheetCommon.amountRequested')}
        isMoney
        disabled={disabled}
        fullWidth
      />
    </Grid>
  )
}

type TermConstraint = FieldValues & {
  term: number | null
  paymentPlanId: string | null
  paymentFrequency: EPaymentFrequency
  activationDate: DateOnly | null
  firstPaymentDate: DateOnly | null
  amountRequested: number | null
  includeProtection: boolean
}

const MERGED_TERM_PREREQUISITES = [
  { name: 'paymentFrequency', prefixKey: 'worksheetCommon' },
  { name: 'activationDate', prefixKey: 'worksheetCommon' },
  { name: 'firstPaymentDate', prefixKey: 'worksheetCommon' },
  { name: 'amountRequested', prefixKey: 'worksheetCommon' },
] as const satisfies readonly FieldPrerequisiteSpec<TermConstraint, 'worksheetCommon'>[]

const TERM_WATCH = ['includeProtection', 'term'] as const

interface TermProps<T extends TermConstraint> {
  control: Control<T>
  label?: string
  disabled?: boolean
  // The saved worksheet's (plan, term), kept visible as a disabled row when the server no
  // longer offers it. Only edit pages have one; the calculator leaves it unset.
  storedTermAndPlan?: StoredTermAndPlan | null
}

const Term = <T extends TermConstraint>({ control, label, disabled, storedTermAndPlan }: TermProps<T>) => {
  const { t } = useTranslation()
  const mergedLabel = label ?? t('worksheetCommon.termAndPlan')
  const termName = 'term' as FieldPathByValue<T, number>

  return (
    <Grid size={cellSize}>
      <FieldReadyGate
        control={control}
        requiredFields={MERGED_TERM_PREREQUISITES}
        watch={TERM_WATCH}
        selfName="term"
        shell={
          <ControlledSelect
            name={termName}
            control={control}
            label={mergedLabel}
            options={[]}
            parseValue={(v) => (v !== null && v !== '' ? Number(v) : null)}
            disabled
            disableEmptyOption
            fullWidth
          />
        }
      >
        {(values) => (
          <TermPlanSelect
            control={control}
            label={label}
            disabled={!!disabled}
            storedTermAndPlan={storedTermAndPlan}
            paymentFrequency={values.paymentFrequency}
            deliveryOn={values.activationDate!}
            firstPaymentOn={values.firstPaymentDate!}
            amountRequested={values.amountRequested!}
            includeInsurance={values.includeProtection}
          />
        )}
      </FieldReadyGate>
    </Grid>
  )
}

interface PaymentFrequencyProps<T extends FieldValues & { paymentFrequency: EPaymentFrequency }> {
  control: Control<T>
  locked?: boolean
  label?: string
}

const PaymentFrequency = <T extends FieldValues & { paymentFrequency: EPaymentFrequency }>({
  control,
  locked,
  label,
}: PaymentFrequencyProps<T>) => {
  const { t } = useTranslation()
  const { supportedPaymentFrequencies, isSinglePayment } = useBaseWorksheetFormConfig()
  const frequencies = useMemo(
    () => (isSinglePayment ? [EPaymentFrequency.Single] : supportedPaymentFrequencies),
    [isSinglePayment, supportedPaymentFrequencies],
  )
  const options = useTranslatedOptionEnum('EPaymentFrequency', frequencies, 'enum')
  const isLocked = locked ?? frequencies.length <= 1

  const fieldName = 'paymentFrequency' as FieldPathByValue<T, EPaymentFrequency>
  const { field } = useController({ control, name: fieldName })

  useEffect(() => {
    if (isSinglePayment && field.value !== EPaymentFrequency.Single) {
      field.onChange(EPaymentFrequency.Single)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSinglePayment])

  return (
    <Grid size={cellSize}>
      <ControlledSelect
        name={fieldName}
        control={control}
        label={label ?? t('worksheetCommon.paymentFrequency')}
        options={options}
        disabled={isLocked}
        disableEmptyOption
        fullWidth
      />
    </Grid>
  )
}

interface ActivationDateProps<T extends FieldValues & { activationDate: DateOnly | null }> {
  control: Control<T>
  maxDate?: DateOnly
  label?: string
  disabled?: boolean
}

const ActivationDate = <T extends FieldValues & { activationDate: DateOnly | null }>({
  control,
  maxDate,
  label,
  disabled,
}: ActivationDateProps<T>) => {
  const { t } = useTranslation()
  const { editLocked } = useBaseWorksheetFormConfig()
  return (
    <Grid size={cellSize}>
      <ControlledDateOnlyPicker
        name={'activationDate' as FieldPathByValue<T, DateOnly>}
        control={control}
        label={label ?? t('worksheetCommon.activationDate')}
        disablePast
        maxDate={maxDate}
        disabled={!!editLocked || !!disabled}
        fullWidth
      />
    </Grid>
  )
}

type FirstPaymentDateConstraint = FieldValues & {
  firstPaymentDate: DateOnly | null
  activationDate: DateOnly | null
  paymentFrequency: EPaymentFrequency
  isHighEstimate?: boolean
}

const FIRST_PAYMENT_DATE_PREREQUISITES = [
  { name: 'activationDate', prefixKey: 'worksheetCommon' },
  { name: 'paymentFrequency', prefixKey: 'worksheetCommon' },
] as const satisfies readonly FieldPrerequisiteSpec<FirstPaymentDateConstraint, 'worksheetCommon'>[]

const FIRST_PAYMENT_DATE_WATCH = ['isHighEstimate'] as const

interface FirstPaymentDateProps<T extends FirstPaymentDateConstraint> {
  control: Control<T>
  label?: string
  disabled?: boolean
  minDate?: DateOnly
}

const FirstPaymentDate = <T extends FirstPaymentDateConstraint>({
  control,
  label,
  disabled,
  minDate,
}: FirstPaymentDateProps<T>) => {
  const { t } = useTranslation()
  const fieldName = 'firstPaymentDate' as FieldPathByValue<T, DateOnly | null>
  const fieldLabel = label ?? t('worksheetCommon.firstPaymentDate')

  return (
    <Grid size={cellSize}>
      <FieldReadyGate
        control={control}
        requiredFields={FIRST_PAYMENT_DATE_PREREQUISITES}
        watch={FIRST_PAYMENT_DATE_WATCH}
        selfName="firstPaymentDate"
        shell={
          <ControlledSelect
            name={fieldName}
            control={control}
            label={fieldLabel}
            options={[]}
            parseValue={(v) => (v ? new DateOnly(v) : null)}
            disabled
            disableEmptyOption
            fullWidth
          />
        }
      >
        {(values) => (
          <FirstPaymentDateActive
            control={control}
            fieldName={fieldName}
            fieldLabel={fieldLabel}
            activationDate={values.activationDate}
            paymentFrequency={values.paymentFrequency}
            isHighEstimate={values.isHighEstimate}
            disabled={disabled}
            minDate={minDate}
          />
        )}
      </FieldReadyGate>
    </Grid>
  )
}

interface FirstPaymentDateActiveProps<T extends FirstPaymentDateConstraint> {
  control: Control<T>
  fieldName: FieldPathByValue<T, DateOnly | null>
  fieldLabel: string
  activationDate: DateOnly | null
  paymentFrequency: EPaymentFrequency
  isHighEstimate: boolean | undefined
  disabled?: boolean
  minDate?: DateOnly
}

const FirstPaymentDateActiveImpl = <T extends FirstPaymentDateConstraint>({
  control,
  fieldName,
  fieldLabel,
  activationDate,
  paymentFrequency,
  isHighEstimate,
  disabled,
  minDate,
}: FirstPaymentDateActiveProps<T>) => {
  const { financingProgramId, creditApplicationId } = useBaseWorksheetFormConfig()

  const optionsDto = useMemo<FirstPaymentDateOptionsDto | null>(() => {
    if (!paymentFrequency || !activationDate) return null
    return {
      deliveryOn: activationDate,
      systemOriginId: EOriginSystemId.Backoffice,
      isHighEstimate: isHighEstimate ?? false,
      paymentFrequency,
    }
  }, [activationDate, paymentFrequency, isHighEstimate])

  const [apiOptions, isFetching] = useGetFirstPaymentDateOptions(financingProgramId, creditApplicationId, optionsDto)
  useReportFieldLoading('firstPaymentDate', isFetching)

  const dateOptions = useMemo(() => {
    const options = apiOptions.map((d) => new DateOnly(d))
    return minDate ? options.filter((d) => d.toString() >= minDate.toString()) : options
  }, [apiOptions, minDate])

  const { isValid, allowedKeys } = useReconcileFieldToOptions({
    control,
    name: fieldName,
    options: dateOptions,
    getKey: (d) => d.toString(),
    enabled: dateOptions.length > 0,
  })

  const selectOptions = useMemo(
    () => renderSelectMenuItems(dateOptions, (d) => ({ value: d.toString(), label: formatDate(d) })),
    [dateOptions],
  )

  if (isFetching || dateOptions.length === 0 || !isValid) {
    return (
      <ControlledSelect
        name={fieldName}
        control={control}
        label={fieldLabel}
        options={[]}
        parseValue={(v) => (v ? new DateOnly(v) : null)}
        disabled
        loading={isFetching}
        disableEmptyOption
        fullWidth
      />
    )
  }

  const useDatePicker = dateOptions.length > FIRST_PAYMENT_DATE_PICKER_THRESHOLD

  return useDatePicker ? (
    <ControlledDateOnlyPicker
      name={fieldName}
      control={control}
      label={fieldLabel}
      minDate={dateOptions[0]}
      maxDate={dateOptions[dateOptions.length - 1]}
      shouldDisableDate={(d: Date) => !allowedKeys.has(new DateOnly(d).toString())}
      disabled={disabled}
      fullWidth
    />
  ) : (
    <ControlledSelect
      name={fieldName}
      control={control}
      label={fieldLabel}
      options={selectOptions}
      parseValue={(v) => (v ? new DateOnly(v) : null)}
      disableEmptyOption
      disabled={disabled}
      fullWidth
    />
  )
}

const FirstPaymentDateActive = memo(FirstPaymentDateActiveImpl) as typeof FirstPaymentDateActiveImpl

interface IsHighEstimateProps<T extends FieldValues & { isHighEstimate: boolean }> {
  control: Control<T>
  label?: string
  disabled?: boolean
}

const IsHighEstimate = <T extends FieldValues & { isHighEstimate: boolean }>({
  control,
  label,
  disabled,
}: IsHighEstimateProps<T>) => {
  const { t } = useTranslation()
  const { canAcceptHighEstimate, supportsHighEstimate } = useBaseWorksheetFormConfig()
  const featureFlags = useFeatureFlags()

  const fieldName = 'isHighEstimate' as FieldPathByValue<T, boolean>
  const { field } = useController({ control, name: fieldName })
  const paymentPlanId = useWatch({ control, name: 'paymentPlanId' as FieldPath<T> }) as string | undefined
  const onPromoPlan = isPromoPlan(paymentPlanId)

  useEffect(() => {
    if (!featureFlags.useHighEstimateOnBuyDown && onPromoPlan && field.value) {
      field.onChange(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [featureFlags.useHighEstimateOnBuyDown, onPromoPlan])

  if (!supportsHighEstimate || canAcceptHighEstimate !== true) return null

  return (
    <Grid size={cellSize}>
      <ControlledCheckbox
        name={fieldName}
        control={control}
        label={label ?? t('worksheetCommon.checkIsHighEstimate')}
        disabled={disabled}
      />
    </Grid>
  )
}

interface IncludeProtectionProps<T extends FieldValues & { includeProtection: boolean }> {
  control: Control<T>
  label?: string
  disabled?: boolean
}

const IncludeProtection = <T extends FieldValues & { includeProtection: boolean }>({
  control,
  label,
  disabled,
}: IncludeProtectionProps<T>) => {
  const { t } = useTranslation()
  const { supportsInsurance } = useBaseWorksheetFormConfig()

  if (!supportsInsurance) return null

  return (
    <Grid size={cellSize}>
      <ControlledCheckbox
        name={'includeProtection' as FieldPathByValue<T, boolean>}
        control={control}
        label={label ?? t('worksheetCommon.protectionPlan')}
        disabled={disabled}
      />
    </Grid>
  )
}

BaseWorksheetForm.AmountRequested = memo(AmountRequested) as typeof AmountRequested
BaseWorksheetForm.Term = memo(Term) as typeof Term
BaseWorksheetForm.PaymentFrequency = memo(PaymentFrequency) as typeof PaymentFrequency
BaseWorksheetForm.ActivationDate = memo(ActivationDate) as typeof ActivationDate
BaseWorksheetForm.FirstPaymentDate = memo(FirstPaymentDate) as typeof FirstPaymentDate
BaseWorksheetForm.IncludeProtection = memo(IncludeProtection) as typeof IncludeProtection
BaseWorksheetForm.IsHighEstimate = memo(IsHighEstimate) as typeof IsHighEstimate

export default BaseWorksheetForm
